// packages/web/src/utils/subscriptionInterceptor.ts
import axios, { AxiosError, AxiosInstance } from 'axios';

// Event emitter for subscription errors
export type SubscriptionErrorEvent = {
  feature: string;
  limit: number;
  used: number;
  upgrade: boolean;
  currentPlan: string;
  message: string;
};

export type SubscriptionErrorListener = (error: SubscriptionErrorEvent) => void;

class SubscriptionErrorEmitter {
  private listeners: SubscriptionErrorListener[] = [];

  addListener(listener: SubscriptionErrorListener) {
    this.listeners.push(listener);
    return () => this.removeListener(listener);
  }

  removeListener(listener: SubscriptionErrorListener) {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  emit(error: SubscriptionErrorEvent) {
    this.listeners.forEach(listener => listener(error));
  }
}

export const subscriptionErrorEmitter = new SubscriptionErrorEmitter();

// Function to setup the axios interceptor
export const setupSubscriptionInterceptor = (axiosInstance: AxiosInstance) => {
  // Response interceptor
  axiosInstance.interceptors.response.use(
    response => response,
    (error: AxiosError) => {
      // Check if the error is a 403 Forbidden with subscription error details
      if (error.response?.status === 403) {
        const errorData = error.response.data as any;
        
        // Check if this is a subscription limit error based on specific fields
        if (errorData.error === 'Usage limit reached' || 
            errorData.error === 'Rate limit exceeded' ||
            errorData.error === 'Subscription required' ||
            errorData.feature) {
          
          // Create a standardized error event
          const errorEvent: SubscriptionErrorEvent = {
            feature: errorData.feature || 'unknown',
            limit: errorData.limit || 0,
            used: errorData.used || 0,
            upgrade: errorData.upgrade !== false, // Default to true
            currentPlan: errorData.currentPlan || 'free',
            message: errorData.message || errorData.error || 'Usage limit reached'
          };
          
          // Emit the error event for components to listen to
          subscriptionErrorEmitter.emit(errorEvent);
          
          // Enhance the error object with the standardized error event
          error.response.data = {
            ...errorData,
            subscriptionError: errorEvent
          };
        }
      }
      
      // Continue with the error
      return Promise.reject(error);
    }
  );

  return axiosInstance;
};

// Function to listen for subscription errors in components
export const useSubscriptionErrorListener = (listener: SubscriptionErrorListener) => {
  return subscriptionErrorEmitter.addListener(listener);
};

// Create a preconfigured axios instance with the interceptor
export const createAxiosWithSubscriptionInterceptor = () => {
  const instance = axios.create({
    baseURL: '/api',
    headers: {
      'Content-Type': 'application/json'
    },
    withCredentials: true
  });
  
  return setupSubscriptionInterceptor(instance);
};